import type { Monitor, MonitorFilters, MonitorSortKey } from '../types/Monitor';
import { calculatePpi } from '../utils/ppiCalculator';

// ── API configuration ─────────────────────────────────────────────────────────
// In production builds (NODE_ENV === 'production') the app calls the real PHP
// API.  In development it falls back to the in-process mock below.
// Override with REACT_APP_API_BASE_URL to point a dev build at a real API.
const API_BASE: string =
  process.env.REACT_APP_API_BASE_URL ?? '/api';

const USE_REAL_API: boolean =
  process.env.NODE_ENV === 'production' ||
  Boolean(process.env.REACT_APP_API_BASE_URL);

/*
 * Production API: GET /api/monitors
 * ─────────────────────────────────────────────────────────────────────────────
 * Query params:
 *   search?:        string                    — free-text filter on name/brand
 *   minDiagonal?:   number                    — minimum diagonal in inches
 *   maxDiagonal?:   number                    — maximum diagonal in inches
 *   panelTypes?:    comma-separated string     — e.g. "IPS,OLED"
 *   minRefreshRate?: number
 *   minPpi?:        number
 *   maxPriceUSD?:   number
 *   hdrOnly?:       boolean
 *   sort?:          'relevance'|'priceLow'|'priceHigh'|'ppi'|'sizeLow'|'sizeHigh'
 *   page?:          number                    — 1-indexed
 *   pageSize?:      number                    — default 20
 *
 * Response: 200 OK
 * {
 *   data:    Monitor[],
 *   total:   number,    — total matching records (for pagination)
 *   page:    number,
 *   pages:   number
 * }
 *
 * GET /api/monitors/:id
 * Response: 200 OK  →  Monitor
 *           404     →  { error: "Not found" }
 */

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_MONITORS: Monitor[] = [
  {
    id: 'lg-27gn950-b',
    name: 'UltraGear 27GN950-B',
    brand: 'LG',
    diagonalInches: 27,
    nativeResolutionW: 3840,
    nativeResolutionH: 2160,
    ppi: calculatePpi(3840, 2160, 27),
    panelType: 'IPS',
    refreshRateHz: 144,
    hdr: true,
    brightnessNits: 400,
    priceUSD: 799,
    ports: ['HDMI 2.1', 'DisplayPort 1.4', 'USB-A × 2'],
    imageUrl: null,
  },
  {
    id: 'dell-u2723qe',
    name: 'UltraSharp U2723QE',
    brand: 'Dell',
    diagonalInches: 27,
    nativeResolutionW: 3840,
    nativeResolutionH: 2160,
    ppi: calculatePpi(3840, 2160, 27),
    panelType: 'IPS',
    refreshRateHz: 60,
    hdr: false,
    brightnessNits: 400,
    priceUSD: 699,
    ports: ['HDMI 2.0', 'DisplayPort 1.4', 'USB-C 90W', 'USB-A × 4'],
    imageUrl: null,
  },
  {
    id: 'apple-studio-display',
    name: 'Studio Display',
    brand: 'Apple',
    diagonalInches: 27,
    nativeResolutionW: 5120,
    nativeResolutionH: 2880,
    ppi: calculatePpi(5120, 2880, 27),
    panelType: 'IPS',
    refreshRateHz: 60,
    hdr: false,
    brightnessNits: 600,
    priceUSD: 1599,
    ports: ['Thunderbolt 3', 'USB-C × 3'],
    imageUrl: null,
    // macOS System Settings → Displays presets (5120×2880 native)
    scalingPresets: [
      { label: 'Larger Text',  logicalW: 2048, logicalH: 1152 },
      { label: 'Default',      logicalW: 2560, logicalH: 1440, isDefault: true },
      { label: '',             logicalW: 3008, logicalH: 1692 },
      { label: 'More Space',   logicalW: 3360, logicalH: 1890 },
    ],
  },
  {
    id: 'samsung-odyssey-g7',
    name: 'Odyssey G7 32"',
    brand: 'Samsung',
    diagonalInches: 32,
    nativeResolutionW: 2560,
    nativeResolutionH: 1440,
    ppi: calculatePpi(2560, 1440, 32),
    panelType: 'VA',
    refreshRateHz: 240,
    hdr: true,
    brightnessNits: 600,
    priceUSD: 649,
    ports: ['HDMI 2.1 × 2', 'DisplayPort 1.4'],
    imageUrl: null,
  },
  {
    id: 'lg-c3-oled-42',
    name: 'OLED C3 42"',
    brand: 'LG',
    diagonalInches: 42,
    nativeResolutionW: 3840,
    nativeResolutionH: 2160,
    ppi: calculatePpi(3840, 2160, 42),
    panelType: 'OLED',
    refreshRateHz: 120,
    hdr: true,
    brightnessNits: 800,
    priceUSD: 1099,
    ports: ['HDMI 2.1 × 4', 'USB-A × 3'],
    imageUrl: null,
  },
  {
    id: 'asus-proart-pa27ucx',
    name: 'ProArt PA27UCX-K',
    brand: 'Asus',
    diagonalInches: 27,
    nativeResolutionW: 3840,
    nativeResolutionH: 2160,
    ppi: calculatePpi(3840, 2160, 27),
    panelType: 'Mini-LED',
    refreshRateHz: 60,
    hdr: true,
    brightnessNits: 1200,
    priceUSD: 1999,
    ports: ['Thunderbolt 3 × 2', 'HDMI 2.0', 'DisplayPort 1.4'],
    imageUrl: null,
  },
  {
    id: 'benq-pd3220u',
    name: 'DesignVue PD3220U',
    brand: 'BenQ',
    diagonalInches: 32,
    nativeResolutionW: 3840,
    nativeResolutionH: 2160,
    ppi: calculatePpi(3840, 2160, 32),
    panelType: 'IPS',
    refreshRateHz: 60,
    hdr: false,
    brightnessNits: 350,
    priceUSD: 999,
    ports: ['Thunderbolt 3 × 2', 'HDMI 2.0', 'DisplayPort 1.4', 'USB-A × 3'],
    imageUrl: null,
  },
  {
    id: 'apple-mbp-14-m3',
    name: 'MacBook Pro 14" (M3)',
    brand: 'Apple',
    diagonalInches: 14.2,
    nativeResolutionW: 3024,
    nativeResolutionH: 1964,
    ppi: calculatePpi(3024, 1964, 14.2),
    panelType: 'Mini-LED',
    refreshRateHz: 120,
    hdr: true,
    brightnessNits: 1000,
    priceUSD: 1999,
    ports: ['Thunderbolt 4 × 3', 'HDMI 2.1', 'SD Card', 'MagSafe 3'],
    imageUrl: null,
    // macOS System Settings → Displays presets (3024×1964 native)
    scalingPresets: [
      { label: 'Larger Text', logicalW: 1024, logicalH: 640  },
      { label: '',            logicalW: 1152, logicalH: 720  },
      { label: '',            logicalW: 1296, logicalH: 810  },
      { label: 'Default',     logicalW: 1512, logicalH: 982, isDefault: true },
      { label: 'More Space',  logicalW: 1728, logicalH: 1080 },
    ],
  },
  {
    id: 'apple-mbp-16-m3',
    name: 'MacBook Pro 16" (M3)',
    brand: 'Apple',
    diagonalInches: 16.2,
    nativeResolutionW: 3456,
    nativeResolutionH: 2234,
    ppi: calculatePpi(3456, 2234, 16.2),
    panelType: 'Mini-LED',
    refreshRateHz: 120,
    hdr: true,
    brightnessNits: 1000,
    priceUSD: 2499,
    ports: ['Thunderbolt 4 × 3', 'HDMI 2.1', 'SD Card', 'MagSafe 3'],
    imageUrl: null,
    // macOS System Settings → Displays presets (3456×2234 native)
    scalingPresets: [
      { label: 'Larger Text', logicalW: 1152, logicalH: 720  },
      { label: '',            logicalW: 1312, logicalH: 820  },
      { label: '',            logicalW: 1496, logicalH: 935  },
      { label: 'Default',     logicalW: 1728, logicalH: 1080, isDefault: true },
      { label: 'More Space',  logicalW: 1920, logicalH: 1200 },
    ],
  },
  {
    id: 'apple-mba-13-m1',
    name: 'MacBook Air 13" (M1)',
    brand: 'Apple',
    diagonalInches: 13.3,
    nativeResolutionW: 2560,
    nativeResolutionH: 1600,
    ppi: calculatePpi(2560, 1600, 13.3),
    panelType: 'IPS',
    refreshRateHz: 60,
    hdr: false,
    brightnessNits: 400,
    priceUSD: 999,
    ports: ['Thunderbolt 4 × 2', 'MagSafe'],
    imageUrl: null,
    // macOS System Settings → Displays presets (2560×1600 native)
    scalingPresets: [
      { label: 'Larger Text', logicalW: 1024, logicalH: 640 },
      { label: '',            logicalW: 1280, logicalH: 800 },
      { label: 'Default',     logicalW: 1440, logicalH: 900, isDefault: true },
      { label: 'More Space',  logicalW: 1680, logicalH: 1050 },
    ],
  },
];

// ── Service ───────────────────────────────────────────────────────────────────

function applyFilters(monitors: Monitor[], filters: MonitorFilters): Monitor[] {
  return monitors.filter((m) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!m.name.toLowerCase().includes(q) && !m.brand.toLowerCase().includes(q)) return false;
    }
    if (filters.brands?.length && !filters.brands.includes(m.brand)) return false;
    if (filters.minDiagonal !== undefined && m.diagonalInches < filters.minDiagonal) return false;
    if (filters.maxDiagonal !== undefined && m.diagonalInches > filters.maxDiagonal) return false;
    if (filters.panelTypes?.length && !filters.panelTypes.includes(m.panelType)) return false;
    if (filters.resolutionTiers?.length) {
      const h = m.nativeResolutionH;
      const match = filters.resolutionTiers.some((tier) => {
        if (tier === 'FHD') return h >= 1080 && h < 1440;
        if (tier === 'QHD') return h >= 1440 && h < 2160;
        if (tier === '4K')  return h >= 2160 && h < 2880;
        if (tier === '5K')  return h >= 2880;
        return false;
      });
      if (!match) return false;
    }
    if (filters.minRefreshRate !== undefined && m.refreshRateHz < filters.minRefreshRate) return false;
    if (filters.minPpi !== undefined && m.ppi < filters.minPpi) return false;
    if (filters.minBrightnessNits !== undefined && m.brightnessNits < filters.minBrightnessNits) return false;
    if (filters.maxPriceUSD !== undefined && (m.priceUSD === null || m.priceUSD > filters.maxPriceUSD)) return false;
    if (filters.hdrOnly && !m.hdr) return false;
    if (filters.requiredPorts?.length) {
      for (const cat of filters.requiredPorts) {
        if (!m.ports.some((p) => p.includes(cat))) return false;
      }
    }
    return true;
  });
}

function applySort(monitors: Monitor[], sort: MonitorSortKey): Monitor[] {
  const sorted = [...monitors];
  switch (sort) {
    case 'priceLow':
      return sorted.sort((a, b) => (a.priceUSD ?? Infinity) - (b.priceUSD ?? Infinity));
    case 'priceHigh':
      return sorted.sort((a, b) => (b.priceUSD ?? -Infinity) - (a.priceUSD ?? -Infinity));
    case 'ppi':
      return sorted.sort((a, b) => b.ppi - a.ppi);
    case 'sizeLow':
      return sorted.sort((a, b) => a.diagonalInches - b.diagonalInches);
    case 'sizeHigh':
      return sorted.sort((a, b) => b.diagonalInches - a.diagonalInches);
    case 'relevance':
    default:
      return sorted;
  }
}

export interface GetMonitorsOptions {
  filters?: MonitorFilters;
  sort?: MonitorSortKey;
}

export interface GetMonitorsResult {
  data: Monitor[];
  total: number;
}

// ── Real-API helpers ──────────────────────────────────────────────────────────

/** Throws a descriptive error when the API response is not OK. */
async function apiFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

/** Converts a MonitorFilters + sort key into a URLSearchParams string. */
function filtersToQueryString(filters: MonitorFilters, sort: MonitorSortKey): string {
  const p = new URLSearchParams();
  if (filters.search)              p.set('search',            filters.search);
  if (filters.brands?.length)      p.set('brands',            filters.brands.join(','));
  if (filters.minDiagonal != null) p.set('minDiagonal',       String(filters.minDiagonal));
  if (filters.maxDiagonal != null) p.set('maxDiagonal',       String(filters.maxDiagonal));
  if (filters.panelTypes?.length)  p.set('panelTypes',        filters.panelTypes.join(','));
  if (filters.resolutionTiers?.length) p.set('resolutionTiers', filters.resolutionTiers.join(','));
  if (filters.minRefreshRate != null)  p.set('minRefreshRate',  String(filters.minRefreshRate));
  if (filters.minPpi != null)          p.set('minPpi',          String(filters.minPpi));
  if (filters.minBrightnessNits != null) p.set('minBrightnessNits', String(filters.minBrightnessNits));
  if (filters.maxPriceUSD != null)     p.set('maxPriceUSD',    String(filters.maxPriceUSD));
  if (filters.hdrOnly)                 p.set('hdrOnly',        '1');
  if (filters.requiredPorts?.length)   p.set('requiredPorts',  filters.requiredPorts.join(','));
  if (sort !== 'relevance')            p.set('sort',           sort);
  return p.toString();
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns every monitor in the catalogue, unfiltered and unsorted.
 * Useful for UI elements that need the full list (e.g. "pick your screen" setup forms).
 */
export async function getAllMonitors(): Promise<Monitor[]> {
  if (!USE_REAL_API) {
    return [...MOCK_MONITORS];
  }
  const result = await apiFetch<{ data: Monitor[] }>(`${API_BASE}/monitors?pageSize=100`);
  return result.data;
}

/**
 * Fetches a filtered and sorted list of monitors.
 */
export async function getMonitors(options: GetMonitorsOptions = {}): Promise<GetMonitorsResult> {
  const { filters = {}, sort = 'relevance' } = options;

  if (!USE_REAL_API) {
    const filtered = applyFilters(MOCK_MONITORS, filters);
    const sorted = applySort(filtered, sort);
    return { data: sorted, total: sorted.length };
  }

  const qs = filtersToQueryString(filters, sort);
  const url = `${API_BASE}/monitors${qs ? `?${qs}` : ''}`;
  const result = await apiFetch<GetMonitorsResult>(url);
  return result;
}

/**
 * Fetches a single monitor by ID.
 */
export async function getMonitorById(id: string): Promise<Monitor | null> {
  if (!USE_REAL_API) {
    return MOCK_MONITORS.find((m) => m.id === id) ?? null;
  }

  try {
    return await apiFetch<Monitor>(`${API_BASE}/monitors/${encodeURIComponent(id)}`);
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('API error 404')) return null;
    throw err;
  }
}
